import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import * as React from "react";

import {
  getTrainingMeStatus,
  listTrainingModules,
  type TrainingMeStatusItem,
  type TrainingModuleListItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** CHP-604: debounced value — same local idiom as admin/users-table.tsx. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** CHP-604: module-status filter vocabulary (pill chips, unit-13 pattern). A
 * module with no status row reads as not started. */
type ModuleStatusFilter = "all" | "completed" | "in_progress" | "not_started";
const MODULE_STATUS_CHIPS: { value: ModuleStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
  { value: "in_progress", label: "In progress" },
  { value: "not_started", label: "Not started" },
];
function moduleStatusKey(st: TrainingMeStatusItem | undefined): Exclude<ModuleStatusFilter, "all"> {
  if (st?.status === "completed") return "completed";
  if (st?.status === "in_progress") return "in_progress";
  return "not_started";
}

/**
 * Trainee Training hub (RACCPE-6 / Slice 4, completion wired RACCPE-14 / Slice 5)
 * — replaces the demo mockup at /dashboard/partner/training. Lists published
 * modules with a progress ring + counters + a Learning Path grid.
 *
 * Completed / In-progress / Remaining are derived from GET /training/me/status
 * (per-module status for the current user) across all published modules; the
 * ring shows completed / total. The demo's SPIF unlock callout is intentionally
 * dropped (SPIFs parked for v1).
 *
 * Styling reuses the demo wireframe utility classes (rd-card, chip, display,
 * mono-xs, page); those are scoped under `.demo-theme`, so the page opts in
 * via a wrapper (same pattern as the login marketing panel).
 */
/**
 * Per-module status chip on a Learning-path card: Completed / In progress /
 * Not started. CHP-894: the "Outstanding" (overdue compulsory) tag and the due
 * date are hidden for now — the designer deferred that feature — so a started
 * module reads "In progress" regardless of its grace deadline.
 */
function ModuleStatusLine({ status }: { status: TrainingMeStatusItem | undefined }) {
  if (!status) return null;
  if (status.status === "completed") {
    return (
      <span className="chip accent" style={{ alignSelf: "flex-start" }}>
        <span className="dot ok" /> Completed
      </span>
    );
  }
  if (status.status === "in_progress") {
    return (
      <span className="chip" style={{ alignSelf: "flex-start" }}>
        In progress
      </span>
    );
  }
  return (
    <span className="chip muted" style={{ alignSelf: "flex-start" }}>
      Not started
    </span>
  );
}

/** One learning-path card grid (CHP-603: rendered once per section). CHP-1013:
 * `viewOnly` (CM) drops the personal-progress chrome — neutral "View" CTA, no
 * status chip, no mandatory-red urgency styling. */
function ModuleGrid({
  modules,
  statusByModule,
  viewOnly = false,
}: {
  modules: TrainingModuleListItem[];
  statusByModule: Map<string, TrainingMeStatusItem>;
  viewOnly?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
        gap: 14,
      }}
    >
      {modules.map((m) => {
        const st = statusByModule.get(m.id);
        const ctaLabel = viewOnly
          ? "View"
          : st?.status === "completed"
            ? "Review"
            : st?.status === "in_progress"
              ? "Continue"
              : "Start";
        // CHP-896: a not-yet-started MANDATORY module gets a red Start
        // button (true danger red, distinct from the brand "pink"
        // accent) to signal it's compulsory.
        const mandatoryStart = m.is_compulsory && ctaLabel === "Start";
        return (
          <div
            key={m.id}
            className="rd-card"
            style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>{m.title}</div>
              {m.is_compulsory && (
                <span className="chip accent" style={{ padding: "2px 8px", fontSize: 9.5 }}>
                  Mandatory
                </span>
              )}
            </div>
            {m.description && (
              <div style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 }}>
                {m.description}
              </div>
            )}
            {!viewOnly && <ModuleStatusLine status={st} />}
            <div style={{ marginTop: "auto", display: "flex", justifyContent: "flex-end" }}>
              <Link
                to="/dashboard/partner/training/$moduleId"
                params={{ moduleId: m.id }}
                className={mandatoryStart ? "rd-btn sm" : "rd-btn ghost sm"}
                style={
                  mandatoryStart
                    ? {
                        padding: "4px 10px",
                        background: "var(--danger)",
                        borderColor: "var(--danger)",
                        color: "#fff",
                        fontWeight: 600,
                      }
                    : { padding: "4px 10px" }
                }
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TrainingHub({ token, viewOnly = false }: { token: string; viewOnly?: boolean }) {
  const modules = useQuery<TrainingModuleListItem[]>({
    queryKey: ["training", "modules"],
    queryFn: () => listTrainingModules(token),
  });
  // CHP-1013: a view-only CM has no personal training journey — skip the
  // per-user status fetch (their counters would be a meaningless 0/N).
  const statuses = useQuery<TrainingMeStatusItem[]>({
    queryKey: ["training", "me-status"],
    queryFn: () => getTrainingMeStatus(token),
    enabled: !viewOnly,
  });

  const items = modules.data ?? [];
  const statusItems = statuses.data ?? [];
  const statusByModule = new Map(statusItems.map((s) => [s.module_id, s]));

  // CHP-604: debounced title/description search + status filter, composed
  // (AND) ahead of the CHP-603 section split.
  const [searchInput, setSearchInput] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<ModuleStatusFilter>("all");
  const query = useDebounced(searchInput, 300).trim().toLowerCase();
  const visibleItems = items.filter((m) => {
    if (
      query !== "" &&
      !m.title.toLowerCase().includes(query) &&
      !(m.description ?? "").toLowerCase().includes(query)
    ) {
      return false;
    }
    return statusFilter === "all" || moduleStatusKey(statusByModule.get(m.id)) === statusFilter;
  });

  // CHP-603: the learning path renders as two headed sections — compulsory
  // modules first, self-paced ones after — using the list item's own flag.
  const mandatoryModules = visibleItems.filter((m) => m.is_compulsory);
  const additionalModules = visibleItems.filter((m) => !m.is_compulsory);
  const completed = statusItems.filter((s) => s.status === "completed").length;
  const inProgress = statusItems.filter((s) => s.status === "in_progress").length;
  const total = statusItems.length;
  const remaining = total - completed - inProgress;

  const r = 52;
  const circumference = 2 * Math.PI * r;
  const ratio = total > 0 ? completed / total : 0;

  return (
    <div className="demo-theme">
      <div className="page">
        <div className="page-head">
          <div>
            <div className="h-eyebrow">Enablement</div>
            <h1 className="h1" style={{ marginTop: 6 }}>
              Knowledge, unlocked.
            </h1>
            <div className="sub">
              {viewOnly
                ? "Browse the training modules partners complete — view-only."
                : "Complete mandatory modules to stay certified and explore more at your own pace."}
            </div>
          </div>
        </div>

        {viewOnly ? null : (
          <div className="rd-card" style={{ marginBottom: 20, padding: 24 }}>
            {statuses.isError ? (
              // CHP-804: a failed status fetch must NOT render as 0/0 / all-zero
              // counters (which read as "nothing started" — a false "all clear").
              <div
                role="alert"
                className="sub"
                style={{ color: "var(--danger, #e73452)" }}
                data-testid="training-progress-error"
              >
                Couldn&apos;t load your training progress. Please refresh to try again.
              </div>
            ) : (
              <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
                <div style={{ position: "relative", width: 120, height: 120, flexShrink: 0 }}>
                  <svg
                    viewBox="0 0 120 120"
                    style={{ width: "100%", height: "100%" }}
                    aria-hidden="true"
                  >
                    <circle
                      cx="60"
                      cy="60"
                      r={r}
                      stroke="var(--bg-3)"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={r}
                      stroke="var(--rd-accent)"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${circumference * ratio} ${circumference}`}
                      strokeLinecap="round"
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div className="display" style={{ fontSize: 30, color: "var(--rd-accent)" }}>
                      {completed}/{total}
                    </div>
                    <div className="mono-xs">complete</div>
                  </div>
                </div>
                <div style={{ flex: 1, display: "flex", gap: 20 }}>
                  <div>
                    <div className="mono-xs">Completed</div>
                    <div
                      className="display"
                      style={{ fontSize: 24, color: "var(--rd-accent)", marginTop: 4 }}
                      data-testid="training-completed"
                    >
                      {completed}
                    </div>
                  </div>
                  <div>
                    <div className="mono-xs">In progress</div>
                    <div
                      className="display"
                      style={{ fontSize: 24, marginTop: 4 }}
                      data-testid="training-in-progress"
                    >
                      {inProgress}
                    </div>
                  </div>
                  <div>
                    <div className="mono-xs">Remaining</div>
                    <div
                      className="display"
                      style={{ fontSize: 24, color: "var(--warn)", marginTop: 4 }}
                      data-testid="training-remaining"
                    >
                      {remaining}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <h2 className="h2">Learning path</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <span className="chip accent">
                <span className="dot ok" /> Mandatory
              </span>
              <span className="chip">Optional</span>
            </div>
          </div>

          {/* CHP-604: standard dashboard filter bar (admin.tsx idiom) —
              debounced search + status pill chips. */}
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
            <div className="relative min-w-[180px] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                placeholder="Search modules"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                aria-label="Search modules"
                data-testid="training-search"
              />
            </div>
            {/* CHP-1013: the status chips filter on PERSONAL progress — hidden
                for a view-only CM (search still applies). */}
            {!viewOnly && (
              <div className="flex flex-wrap items-center gap-2">
                {MODULE_STATUS_CHIPS.map((chip) => (
                  <Button
                    key={chip.value}
                    type="button"
                    size="sm"
                    variant={statusFilter === chip.value ? "default" : "outline"}
                    onClick={() => setStatusFilter(chip.value)}
                    data-testid={`training-status-filter-${chip.value}`}
                  >
                    {chip.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {modules.isLoading ? (
            <div className="sub">Loading…</div>
          ) : modules.isError ? (
            <div className="sub">Could not load training modules. Please try again.</div>
          ) : items.length === 0 ? (
            <div className="sub">No training modules have been published yet.</div>
          ) : visibleItems.length === 0 ? (
            <div className="sub" data-testid="training-no-match">
              No modules match your search or filters.
            </div>
          ) : (
            // CHP-603: mandatory and additional modules render as separate
            // headed grids (was one mixed grid).
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {mandatoryModules.length > 0 && (
                <section data-testid="training-mandatory-section">
                  <h3 className="h2" style={{ fontSize: 16, marginBottom: 10 }}>
                    Mandatory modules
                  </h3>
                  <ModuleGrid
                    modules={mandatoryModules}
                    statusByModule={statusByModule}
                    viewOnly={viewOnly}
                  />
                </section>
              )}
              {additionalModules.length > 0 && (
                <section data-testid="training-additional-section">
                  <h3 className="h2" style={{ fontSize: 16, marginBottom: 10 }}>
                    Additional modules
                  </h3>
                  <ModuleGrid
                    modules={additionalModules}
                    statusByModule={statusByModule}
                    viewOnly={viewOnly}
                  />
                </section>
              )}
            </div>
          )}
        </div>

        {/* CHP-601: the PO "Team training status" card was removed — this page
            is about PERSONAL training; the same TeamTrainingReport already
            lives on the team-insights surface. */}
      </div>
    </div>
  );
}

export function TraineeTrainingRoute() {
  const { accessToken, data: user } = useCurrentUser();
  // CHP-1013: wait for the role before mounting — rendering with an unresolved
  // user would mount the trainee variant for a CM and fire its personal
  // progress queries before viewOnly flips (react-query doesn't cancel an
  // already-issued fetch on an enabled transition).
  if (!accessToken || !user) return null;
  // A Channel Manager browses the same hub view-only (designer ruling) — no
  // personal progress, no trainee CTAs. CHP-1049: a perm-less RA (admin role)
  // reaches this hub read-only too; the list is identical (view-only), and the
  // per-module view adds the answer-key preview. Both CM and admin are view-only.
  return (
    <TrainingHub
      token={accessToken}
      viewOnly={user.role === "channel_manager" || user.role === "admin"}
    />
  );
}
